import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Layout Risk Redux slice. Reports are cached per (projectId + targetLanguage)
// so switching the language tab is instant once each language has been fetched.
// Per-frame Auto-fit drafts live in `drafts`, keyed by frame id, until the user
// applies or discards them.

const reportKey = (projectId, lang) => `${projectId}:${lang}`;

const authHeaders = (thunkAPI) => {
    const token = thunkAPI.getState().auth.token;
    return { headers: { Authorization: `Bearer ${token}` } };
};

// GET cached report for a language.
export const fetchLayoutRiskReport = createAsyncThunk(
    'layoutRisk/fetch',
    async ({ projectId, lang }, thunkAPI) => {
        const res = await axios.get(`/api/layout-risk/${projectId}?lang=${encodeURIComponent(lang)}`, authHeaders(thunkAPI));
        return { projectId, lang, report: res.data.report };
    },
);

// POST run/re-run pre-flight for a language.
export const runLayoutRiskPreflight = createAsyncThunk(
    'layoutRisk/run',
    async ({ projectId, targetLanguage }, thunkAPI) => {
        const res = await axios.post(
            `/api/layout-risk/${projectId}/run`,
            { targetLanguage },
            authHeaders(thunkAPI),
        );
        return { projectId, lang: targetLanguage, report: res.data.report };
    },
);

// Normalize an axios error into { msg, code } from the server payload.
const errPayload = (err, fallback) => ({
    msg: (err.response && err.response.data && err.response.data.msg) || fallback,
    code: err.response && err.response.data && err.response.data.code,
    status: err.response && err.response.status,
});

// POST a single Auto-fit draft (does not write).
export const autofitFrame = createAsyncThunk(
    'layoutRisk/autofit',
    async ({ projectId, frameId, targetLanguage }, thunkAPI) => {
        try {
            const res = await axios.post(
                `/api/layout-risk/${projectId}/autofit`,
                { frameId, targetLanguage },
                authHeaders(thunkAPI),
            );
            return { frameId, draft: res.data.draft };
        } catch (err) {
            return thunkAPI.rejectWithValue(errPayload(err, 'Auto-fit failed.'));
        }
    },
);

// POST apply a fitted draft — writes into translatedSections, returns the
// recomputed report.
export const applyLayoutFix = createAsyncThunk(
    'layoutRisk/applyFix',
    async ({ projectId, frameId, targetLanguage, text }, thunkAPI) => {
        try {
            const res = await axios.post(
                `/api/layout-risk/${projectId}/autofit/apply`,
                { frameId, targetLanguage, text },
                authHeaders(thunkAPI),
            );
            return { projectId, lang: targetLanguage, frameId, report: res.data.report };
        } catch (err) {
            return thunkAPI.rejectWithValue(errPayload(err, 'Failed to apply Auto-fit.'));
        }
    },
);

const initialState = {
    reportsByKey: {},   // "<pid>:<lang>" -> report
    drafts: {},         // frameId -> { text, chars, capacityChars, overBudget, glossaryKept }
    fetchStatus: 'idle',
    running: false,
    fitting: {},        // frameId -> true while a draft is being generated
    applying: {},       // frameId -> true while a fix is being written
    error: null,
};

const layoutRiskSlice = createSlice({
    name: 'layoutRisk',
    initialState,
    reducers: {
        discardDraft: (state, action) => {
            delete state.drafts[action.payload.frameId];
        },
        clearDrafts: (state) => {
            state.drafts = {};
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLayoutRiskReport.pending, (state) => {
                state.fetchStatus = 'loading';
                state.error = null;
            })
            .addCase(fetchLayoutRiskReport.fulfilled, (state, action) => {
                state.fetchStatus = 'succeeded';
                state.reportsByKey[reportKey(action.payload.projectId, action.payload.lang)] = action.payload.report;
            })
            .addCase(fetchLayoutRiskReport.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                // A missing cached report (pre-flight not run) isn't a user-facing error.
            })
            .addCase(runLayoutRiskPreflight.pending, (state) => {
                state.running = true;
                state.error = null;
            })
            .addCase(runLayoutRiskPreflight.fulfilled, (state, action) => {
                state.running = false;
                state.reportsByKey[reportKey(action.payload.projectId, action.payload.lang)] = action.payload.report;
            })
            .addCase(runLayoutRiskPreflight.rejected, (state, action) => {
                state.running = false;
                state.error = action.payload || { msg: action.error.message };
            })
            .addCase(autofitFrame.pending, (state, action) => {
                state.fitting[action.meta.arg.frameId] = true;
                state.error = null;
            })
            .addCase(autofitFrame.fulfilled, (state, action) => {
                delete state.fitting[action.payload.frameId];
                state.drafts[action.payload.frameId] = action.payload.draft;
            })
            .addCase(autofitFrame.rejected, (state, action) => {
                delete state.fitting[action.meta.arg.frameId];
                state.error = action.payload || { msg: action.error.message };
            })
            .addCase(applyLayoutFix.pending, (state, action) => {
                state.applying[action.meta.arg.frameId] = true;
                state.error = null;
            })
            .addCase(applyLayoutFix.fulfilled, (state, action) => {
                delete state.applying[action.payload.frameId];
                delete state.drafts[action.payload.frameId];
                state.reportsByKey[reportKey(action.payload.projectId, action.payload.lang)] = action.payload.report;
            })
            .addCase(applyLayoutFix.rejected, (state, action) => {
                delete state.applying[action.meta.arg.frameId];
                state.error = action.payload || { msg: action.error.message };
            });
    },
});

export const { discardDraft, clearDrafts, clearError } = layoutRiskSlice.actions;
export const selectReport = (state, projectId, lang) => state.layoutRisk.reportsByKey[reportKey(projectId, lang)];
export default layoutRiskSlice.reducer;
